<template>
  <ui-sheet v-model="model" :title="$t('filters')">
    <template #action>
      <button type="button" class="rm-text-btn" @click="resetDraft">
        {{ $t("report_mobile.reset") }}
      </button>
    </template>
    <div v-if="config.warehouse" class="rm-sheet__group" data-filter="warehouse">
      <div class="wm-filter-head">
        <h3 class="rm-sheet__label">{{ $t("stock") }}</h3>
        <span class="rm-muted rm-num">
          {{ $t("report_mobile.selected_of", { selected: draft.warehouseIds.length, total: warehouseItems.length }) }}
        </span>
      </div>
      <div class="wm-checks" role="group" :aria-label="$t('stock')">
        <label v-for="item in warehouseItems" :key="item.id" class="wm-check">
          <input type="checkbox" :checked="draft.warehouseIds.includes(item.id)" @change="toggle('warehouseIds', item.id)" />
          <span>{{ toLatin(item.name) }}</span>
        </label>
      </div>
    </div>

    <div v-if="config.view" class="rm-sheet__group" data-filter="view">
      <h3 class="rm-sheet__label">{{ $t("view") }}</h3>
      <div class="wm-chip-wrap">
        <button
          v-for="item in viewItems"
          :key="item.id"
          type="button"
          class="rm-chip rm-chip--lg"
          :class="{ 'rm-chip--active': draft.showType === item.id }"
          :aria-pressed="String(draft.showType === item.id)"
          @click="draft.showType = item.id"
        >
          {{ item.name }}
        </button>
      </div>
    </div>

    <div v-if="config.shifts" class="rm-sheet__group" data-filter="shifts">
      <h3 class="rm-sheet__label">{{ $t("shifts") }}</h3>
      <div class="wm-chip-wrap">
        <button
          v-for="shift in allShifts"
          :key="shift"
          type="button"
          class="rm-chip rm-chip--lg"
          :class="{ 'rm-chip--active': draft.shifts.includes(shift) }"
          :aria-pressed="String(draft.shifts.includes(shift))"
          @click="toggleShift(shift)"
        >
          {{ $t("report_mobile.shift_n", { n: shift }) }}
        </button>
      </div>
    </div>

    <div v-if="config.docType" class="rm-sheet__group" data-filter="docType">
      <h3 class="rm-sheet__label">{{ $t("warehouse.document_type") }}</h3>
      <div class="wm-chip-wrap">
        <button
          v-for="item in docTypeItems"
          :key="String(item.id)"
          type="button"
          class="rm-chip rm-chip--lg"
          :class="{ 'rm-chip--active': draft.documentType === item.id }"
          :aria-pressed="String(draft.documentType === item.id)"
          @click="draft.documentType = item.id"
        >
          {{ item.name }}
        </button>
      </div>
    </div>

    <div v-if="config.status" class="rm-sheet__group" data-filter="status">
      <h3 class="rm-sheet__label">{{ $t("status") }}</h3>
      <select v-model="draft.status" class="wm-select" :aria-label="$t('status')">
        <option v-for="item in statusItems" :key="String(item.id)" :value="item.id">{{ item.name }}</option>
      </select>
    </div>

    <div v-if="config.suppliers" class="rm-sheet__group" data-filter="suppliers">
      <div class="wm-filter-head">
        <h3 class="rm-sheet__label">{{ $t("supplier") }}</h3>
        <span class="rm-muted rm-num">
          {{ $t("report_mobile.selected_of", { selected: draft.supplierIds.length, total: supplierItems.length }) }}
        </span>
      </div>
      <div class="wm-checks" role="group" :aria-label="$t('supplier')">
        <label v-for="item in supplierItems" :key="item.id" class="wm-check">
          <input type="checkbox" :checked="draft.supplierIds.includes(item.id)" @change="toggle('supplierIds', item.id)" />
          <span>{{ toLatin(item.name) }}</span>
        </label>
      </div>
    </div>

    <div v-if="config.grocery" class="rm-sheet__group" data-filter="grocery">
      <h3 class="rm-sheet__label">{{ $t("code") }}</h3>
      <select v-model="draft.groceryCode" class="wm-select" :aria-label="$t('code')">
        <option v-for="item in groceryItems" :key="String(item.id)" :value="item.id">{{ toLatin(item.name) }}</option>
      </select>
    </div>
    <template #footer>
      <button type="button" class="rm-btn rm-btn--primary" :disabled="!canApply" @click="apply">
        {{ $t("report_mobile.apply") }}
      </button>
    </template>
  </ui-sheet>
</template>

<script>
import hasModel from "@/mixins/hasModel";

const ALL_SHIFTS = [1, 2, 3, 4];

/**
 * Filteri magacina na telefonu u donjoj kartici: samo oni koje ekran koristi (`config`, isti kao traka na računaru).
 * Izbor se primenjuje tek na "Primeni"; "Poništi" vraća sve magacine i dobavljače, sve smene i bez statusa i tipa.
 */
export default {
  name: "WarehouseMobileFilterSheet",
  mixins: [hasModel],
  props: {
    /** koje izbore ekran ima: { warehouse, view, shifts, docType, status, suppliers, grocery } */
    config: { type: Object, default: () => ({}) },
    warehouseItems: { type: Array, default: () => [] },
    selectedWarehouseIds: { type: Array, default: () => [] },
    viewItems: { type: Array, default: () => [] },
    showType: { type: String, default: "daily" },
    shifts: { type: Array, default: () => [1, 2, 3] },
    docTypeItems: { type: Array, default: () => [] },
    documentType: { type: String, default: null },
    statusItems: { type: Array, default: () => [] },
    status: { type: String, default: null },
    supplierItems: { type: Array, default: () => [] },
    selectedSupplierIds: { type: Array, default: () => [] },
    groceryItems: { type: Array, default: () => [] },
    groceryCode: { type: String, default: null },
  },
  data: () => ({
    allShifts: ALL_SHIFTS,
    draft: {
      warehouseIds: [],
      showType: "daily",
      shifts: [],
      documentType: null,
      status: null,
      supplierIds: [],
      groceryCode: null,
    },
  }),
  computed: {
    canApply() {
      return !(this.config.warehouse && !this.draft.warehouseIds.length);
    },
  },
  watch: {
    value: {
      handler(isOpen) {
        if (isOpen) {
          this.draftFromCurrent();
        }
      },
      immediate: true,
    },
  },
  methods: {
    draftFromCurrent() {
      this.draft = {
        warehouseIds: [...this.selectedWarehouseIds],
        showType: this.showType,
        shifts: [...this.shifts],
        documentType: this.documentType,
        status: this.status,
        supplierIds: [...this.selectedSupplierIds],
        groceryCode: this.groceryCode,
      };
    },
    resetDraft() {
      this.draft = {
        warehouseIds: this.warehouseItems.map((item) => item.id),
        showType: this.draft.showType,
        shifts: [...ALL_SHIFTS],
        documentType: null,
        status: null,
        supplierIds: this.supplierItems.map((item) => item.id),
        groceryCode: null,
      };
    },
    toggle(key, id) {
      const list = this.draft[key];
      this.draft[key] = list.includes(id) ? list.filter((item) => item !== id) : [...list, id];
    },
    toggleShift(shift) {
      const selected = this.draft.shifts.includes(shift);
      if (selected && this.draft.shifts.length === 1) {
        return;
      }
      this.draft.shifts = (selected ? this.draft.shifts.filter((item) => item !== shift) : [...this.draft.shifts, shift]).sort();
    },
    apply() {
      this.$emit("apply", { ...this.draft });
      this.model = false;
    },
  },
};
</script>
